import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { QUEUE_LANE, type QueueLane } from '@vagusx/shared';
import type { CreateExclusionInput, QueuePage } from '@vagusx/clients';
import { getOutreachClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';
import { CACHE_TIME_1_MIN, POLL_TIME_LIVE, POLL_TIME_LIVE_BADGE } from '@/lib/query-client';

/**
 * A campaign's queue (ADR-318, F/H). Rows carry patient names (PHI): shown on
 * screen under `outreach:view`, never logged.
 */

/** Rows per server page; the table's pager fetches the next one when a page reaches past it. */
export const QUEUE_PAGE_SIZE = 100;

/**
 * The lane counts. Each read recomputes membership on the server, so while the
 * Calling lane is open they refresh at the badge interval, not the live one;
 * the Calling count itself is taken from the lane's own polled rows (QueueTab).
 */
export function useQueueSummary(
  campaignId: string,
  lane?: QueueLane,
  opts?: { enabled?: boolean }
) {
  const live = lane === QUEUE_LANE.CALLING;
  return useQuery({
    queryKey: queryKeys.outreach.queueSummary(campaignId),
    queryFn: () => getOutreachClient().queueSummary(campaignId),
    enabled: opts?.enabled ?? true,
    staleTime: CACHE_TIME_1_MIN,
    refetchInterval: live ? POLL_TIME_LIVE_BADGE : false,
  });
}

/**
 * One lane, page by page. The Calling lane polls at the live interval — a
 * call starts and ends in minutes — and every other lane at the badge
 * interval, on the scale of the driver's ticks.
 */
export function useQueueLane(campaignId: string, lane: QueueLane, opts?: { enabled?: boolean }) {
  const live = lane === QUEUE_LANE.CALLING;
  return useInfiniteQuery({
    enabled: opts?.enabled ?? true,
    queryKey: queryKeys.outreach.queue(campaignId, lane),
    queryFn: ({ pageParam }) =>
      getOutreachClient().queuePage(campaignId, {
        lane,
        limit: QUEUE_PAGE_SIZE,
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last: QueuePage) => last.nextCursor,
    staleTime: live ? 0 : CACHE_TIME_1_MIN,
    // Every lane refreshes while it is shown (a disabled query never polls):
    // the Calling lane live, the rest at the badge interval, so a row the
    // driver has moved does not linger with a past "Next".
    refetchInterval: live ? POLL_TIME_LIVE : POLL_TIME_LIVE_BADGE,
  });
}

/** Everything a queue change can move: every lane and the counts. */
function invalidateQueue(queryClient: ReturnType<typeof useQueryClient>, campaignId: string): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.outreach.queueSummary(campaignId) });
  void queryClient.invalidateQueries({
    queryKey: [...queryKeys.outreach.campaign(campaignId), 'queue'],
  });
}

/** "Call now": the next try moves to now; every gate still decides. */
export function useRunNow(campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) => getOutreachClient().runNow(runId),
    onSuccess: () => {
      invalidateQueue(queryClient, campaignId);
      toast.success('Moved to the front. It still waits for calling hours and every limit.');
    },
  });
}

/** "Take out": out of this campaign, with or without a run (R5). */
export function useExcludePatient(campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateExclusionInput) =>
      getOutreachClient().excludePatient(campaignId, input),
    onSuccess: () => {
      invalidateQueue(queryClient, campaignId);
      toast.success('Taken out of this campaign');
    },
  });
}
